<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import wameSocialChannel from 'dashboard/api/channel/wameSocialChannel';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WameAuthorizationFrame from './WameAuthorizationFrame.vue';

// Tela única para Instagram Direct e Messenger. Os dois canais chegam pela mesma
// chave WAME e pelo mesmo fluxo de autorização — o que muda é o provider e a
// aparência do botão.
const props = defineProps({
  provider: {
    type: String,
    required: true,
    validator: value => ['instagram', 'messenger'].includes(value),
  },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const isLoading = ref(true);
const isConnecting = ref(false);
const keys = ref([]);
const selectedKey = ref('');
const errorMessage = ref('');
const keyLoadFailed = ref(false);
// A autorização deixou de ser outra página: a partir daqui ela é desenhada
// dentro desta tela. Ver WameAuthorizationFrame.
const authorizationUrl = ref('');
const createdInboxId = ref(null);

const providerKey = computed(() => props.provider.toUpperCase());

const availableKeys = computed(() =>
  keys.value.filter(key =>
    props.provider === 'instagram'
      ? key.instagram_available
      : key.messenger_available
  )
);

// Sem chave não há por onde conectar: a chave da WAME é criada junto com a
// conexão do WhatsApp da empresa, e é ela que carrega os canais sociais.
const hasNoKeys = computed(
  () => !isLoading.value && !keyLoadFailed.value && keys.value.length === 0
);

// Existe chave, mas todas já têm um canal deste tipo. Uma chave não pode
// carregar dois canais do mesmo provider — o roteamento do webhook ficaria
// ambíguo entre eles.
const allKeysInUse = computed(
  () =>
    !isLoading.value &&
    keys.value.length > 0 &&
    availableKeys.value.length === 0
);

const canConnect = computed(
  () => !!selectedKey.value && !isConnecting.value && !isLoading.value
);

const keyLabel = key =>
  key.instance_name
    ? `${key.instance_name} (${key.fingerprint})`
    : key.fingerprint;

// A página da WAME redireciona de volta carimbando o resultado na querystring.
// O contrato real, lido do código-fonte da página deles em 19/08/2026, é
// `?instagram=connected` | `?instagram=error&message=<texto>` (e o mesmo com
// `messenger`). Antes líamos `ig_error=1`/`ig_msg`, que a WAME nunca envia:
// tanto o sucesso quanto a recusa eram descartados em silêncio, e a pessoa
// voltava para uma tela idêntica à que tinha deixado — sem saber, por exemplo,
// que o plano dela não cobre o canal.
const readResultFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get(props.provider);

  if (outcome === 'error') {
    errorMessage.value =
      params.get('message') || t('INBOX_MGMT.ADD.WAME_SOCIAL.ERROR_AUTH');
  }

  if (outcome) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  return outcome;
};

const loadKeys = async () => {
  isLoading.value = true;
  keyLoadFailed.value = false;
  try {
    const { data } = await wameSocialChannel.list();
    keys.value = data.payload.keys || [];
    selectedKey.value = availableKeys.value[0]?.api_key || '';
  } catch (error) {
    keyLoadFailed.value = true;
    keys.value = [];
    selectedKey.value = '';
    errorMessage.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.ADD.WAME_SOCIAL.ERROR_KEYS');
  } finally {
    isLoading.value = false;
  }
};

const connect = async () => {
  isConnecting.value = true;
  errorMessage.value = '';
  try {
    const { data } = await wameSocialChannel.connect({
      provider: props.provider,
      apiKey: selectedKey.value,
      returnUrl: window.location.href.split('?')[0],
    });

    // A inbox já existe a partir daqui; a lista em memória precisa saber disso
    // para não mostrar a conta como ausente quando o usuário voltar do OAuth.
    store.dispatch('inboxes/get');

    // A autorização passa a ser desenhada aqui dentro. No Messenger o ciclo
    // termina sem sair da tela; no Instagram a própria página da WAME sai do
    // iframe (o instagram.com recusa ser embutido) e o usuário volta pelo
    // `return_url`, que é lido em #readResultFromUrl.
    createdInboxId.value = data.payload.inbox_id;
    authorizationUrl.value = data.payload.authorization_url;
    isConnecting.value = false;
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.ADD.WAME_SOCIAL.ERROR_CONNECT');
    isConnecting.value = false;
  }
};

// A WAME avisa por postMessage quando a autorização termina dentro do iframe.
// Nesse instante a inbox já é utilizável: levar o usuário para ela é o passo
// que faltava — antes ele voltava para a mesma tela de conexão, sem sinal de
// que tinha dado certo.
const onAuthorized = () => {
  store.dispatch('inboxes/get');
  if (!createdInboxId.value) return;

  router.replace({
    name: 'settings_inbox_show',
    params: {
      accountId: route.params.accountId,
      inboxId: createdInboxId.value,
    },
  });
};

onMounted(() => {
  // Voltar autorizado significa que a inbox já existe: a lista em memória
  // precisa saber disso para não mostrar a conta como ausente.
  if (readResultFromUrl() === 'connected') {
    store.dispatch('inboxes/get');
  }
  loadKeys();
});
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak max-w-lg w-full"
      >
        <h6 class="text-2xl font-medium">
          {{ $t(`INBOX_MGMT.ADD.WAME_SOCIAL.${providerKey}.TITLE`) }}
        </h6>
        <p class="py-4 text-sm text-n-slate-11">
          {{ $t(`INBOX_MGMT.ADD.WAME_SOCIAL.${providerKey}.HELP`) }}
        </p>

        <Spinner v-if="isLoading" />

        <p v-else-if="keyLoadFailed" class="text-sm text-n-ruby-11">
          {{ errorMessage }}
        </p>

        <p v-else-if="hasNoKeys" class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WAME_SOCIAL.NO_KEYS') }}
        </p>

        <p v-else-if="allKeysInUse" class="text-sm text-n-slate-11">
          {{ $t(`INBOX_MGMT.ADD.WAME_SOCIAL.${providerKey}.ALL_IN_USE`) }}
        </p>

        <WameAuthorizationFrame
          v-else-if="authorizationUrl"
          :url="authorizationUrl"
          :provider="provider"
          @connected="onAuthorized"
        />

        <template v-else>
          <label class="w-full text-left text-sm">
            {{ $t('INBOX_MGMT.ADD.WAME_SOCIAL.CONNECTION_LABEL') }}
            <select v-model="selectedKey" class="w-full mt-1">
              <option
                v-for="key in availableKeys"
                :key="key.api_key"
                :value="key.api_key"
              >
                {{ keyLabel(key) }}
              </option>
            </select>
          </label>
          <p class="pb-4 text-xs text-left text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WAME_SOCIAL.CONNECTION_HELP') }}
          </p>

          <Button
            lg
            :icon="
              provider === 'instagram'
                ? 'i-ri-instagram-line'
                : 'i-ri-messenger-line'
            "
            :disabled="!canConnect"
            :is-loading="isConnecting"
            :label="$t(`INBOX_MGMT.ADD.WAME_SOCIAL.${providerKey}.CTA`)"
            @click="connect"
          />
        </template>

        <p
          v-if="errorMessage && !keyLoadFailed"
          class="pt-4 text-sm text-n-ruby-11"
        >
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>
